import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Grid, BookMarked, LayoutGrid, Layers, BarChart3, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useCollectionStore } from '../lib/collectionStore';
import { getAllSets } from '../services/pokemonTcgApi';
import { useProfileStore } from '../lib/supabase';

interface StatsData {
  totalCards: number;
  uniqueCards: number;
  setsCollected: number;
  seriesCollected: number;
  totalSeries: number;
  totalCardsInDatabase: number;
}

interface CollectionStatsProps {
  setId?: string;
  totalCards?: number;
  stats?: StatsData;
  activeTab?: 'total' | 'unique' | 'series' | 'portfolio';
  onTabChange?: (tab: 'total' | 'unique' | 'series' | 'portfolio') => void;
}

export default function CollectionStats({ setId, totalCards, stats, activeTab, onTabChange }: CollectionStatsProps) {
  const { collection } = useCollectionStore();
  const { isAuthenticated } = useProfileStore();
  const [setStats, setSetStats] = useState({
    uniqueCards: 0,
    totalCards: 0,
    uniqueFoils: 0,
    totalFoils: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [allSets, setAllSets] = useState<any[]>([]);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;
  
  const fetchStats = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        setSetStats({
          uniqueCards: 0,
          totalCards: 0,
          uniqueFoils: 0,
          totalFoils: 0
        });
        setLoading(false);
        return;
      }

      const sets = await getAllSets();
      setAllSets(sets);

      // Get unique card IDs (ignoring finishes)
      const uniqueCardIds = new Set(collection.map(card => card.cardId));
      
      // Calculate total cards including duplicates
      const totalCardCount = collection.reduce((sum, card) => sum + card.quantity, 0);

      // Calculate stats for specific set if setId is provided
      if (setId) {
        const setCards = collection.filter(card => card.setId === setId);
        const uniqueSetCardIds = new Set(setCards.map(card => card.cardId));
        
        setSetStats({
          uniqueCards: uniqueSetCardIds.size,
          totalCards: setCards.reduce((sum, card) => sum + card.quantity, 0),
          uniqueFoils: new Set(setCards.filter(card => card.finish !== 'normal').map(card => card.cardId)).size,
          totalFoils: setCards.filter(card => card.finish !== 'normal').reduce((sum, card) => sum + card.quantity, 0)
        });
      } else {
        // Calculate global stats
        const uniqueSeries = new Set(sets.map(set => set.series));
        const collectedSeries = new Set(collection.map(card => {
          const set = sets.find(s => s.id === card.setId);
          return set?.series;
        }));

        setSetStats({
          uniqueCards: uniqueCardIds.size,
          totalCards: totalCardCount,
          uniqueFoils: new Set(collection.filter(card => card.finish !== 'normal').map(card => card.cardId)).size,
          totalFoils: collection.filter(card => card.finish !== 'normal').reduce((sum, card) => sum + card.quantity, 0)
        });
      }

      setLoading(false);
      setError(null);
    } catch (error) {
      console.error('Error fetching collection stats:', error);
      
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, RETRY_DELAY);
      } else {
        setError(error instanceof Error ? error.message : 'Error loading stats');
      }
    } finally {
      setLoading(false);
    }
  }, [setId, collection, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
    }
  }, [setId, collection, fetchStats, isAuthenticated]);

  const completion = setId
    ? totalCards && totalCards > 0 ? (setStats.uniqueCards / totalCards) * 100 : 0
    : stats?.totalCardsInDatabase > 0 ? (stats.uniqueCards / stats.totalCardsInDatabase) * 100 : 0;

  const clampedCompletion = Math.min(completion, 100);
  const uniqueCount = setId ? setStats.uniqueCards : (stats?.uniqueCards || 0);
  const totalCount = setId ? (totalCards || 0) : (stats?.totalCardsInDatabase || 0);
  const ownedCount = setId ? setStats.totalCards : (stats?.totalCards || 0);

  if (error) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <p className="text-red-500">Error loading collection stats</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="bg-black/95 backdrop-blur-sm border border-white/10 rounded-xl p-4">
        <div className="text-center">
          <p className="text-white/60 mb-2">Sign in to track your collection progress</p>
          <Link
            to="/auth"
            className="inline-block px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black/95 backdrop-blur-sm border border-white/10 rounded-xl p-4 relative overflow-hidden">
      <div className="absolute -top-12 -right-12 w-32 h-32 bg-green-500/5 rounded-full blur-3xl pointer-events-none" />

      {onTabChange && (
        <div className="flex items-center gap-1 mb-4">
          <button
            onClick={() => onTabChange('total')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
              activeTab === 'total' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}
          >
            <Grid size={14} />
            <span>All Cards</span>
          </button>
          <button
            onClick={() => onTabChange('unique')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
              activeTab === 'unique' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}
          >
            <BookMarked size={14} />
            <span>Unique</span>
          </button>
          <button
            onClick={() => onTabChange('series')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
              activeTab === 'series' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}
          >
            <Layers size={14} />
            <span>Series</span>
          </button>
          <button
            onClick={() => onTabChange('portfolio')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors text-sm ${
              activeTab === 'portfolio' ? 'bg-white/20 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
            }`}
          >
            <BarChart3 size={14} />
            <span>Portfolio</span>
          </button>
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
        {/* Progress section */}
        <div className="flex-1 flex flex-col gap-2">
          <h2 className="text-white text-xs font-bold uppercase tracking-wider">Collection Progress</h2>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-white/10 rounded-full relative">
              <div
                className={`absolute top-0 left-0 h-full rounded-full transition-all duration-700 ease-out ${loading ? 'bg-white/20' : 'bg-green-500'}`}
                style={{ width: `${clampedCompletion}%` }}
              >
                {!loading && clampedCompletion > 0 && (
                  <div
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-3 h-3 bg-green-500 rounded-full border-2 border-black"
                    style={{ boxShadow: '0 0 10px 1px rgba(34, 197, 94, 0.3)' }}
                  />
                )}
              </div>
              {loading && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full animate-shimmer" />
              )}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute h-full w-px bg-white/[0.07]" style={{ left: '25%' }} />
                <div className="absolute h-full w-px bg-white/[0.07]" style={{ left: '50%' }} />
                <div className="absolute h-full w-px bg-white/[0.07]" style={{ left: '75%' }} />
              </div>
            </div>
            <div className="flex items-center gap-1 min-w-[70px] justify-end shrink-0">
              <span className="text-sm font-bold text-white">{uniqueCount}</span>
              <span className="text-[10px] text-white/40 font-bold uppercase">/ {totalCount}</span>
            </div>
          </div>
        </div>

        {/* Stats section */}
        <div className="flex items-center gap-3 lg:gap-4 border-t lg:border-t-0 lg:border-l border-white/10 pt-3 lg:pt-0 lg:pl-5 shrink-0">
          <div className="flex flex-col min-w-[60px]">
            <div className="flex items-center gap-1.5 mb-0.5">
              <Layers size={12} className="text-white/40" />
              <span className="text-[10px] font-bold text-white/40 uppercase tracking-tighter">Owned</span>
            </div>
            <span className="text-base font-bold text-white leading-none">{ownedCount}</span>
          </div>

          <div className="flex flex-col min-w-[60px]">
            <div className="flex items-center gap-1.5 mb-0.5">
              <BookMarked size={12} className="text-white/40" />
              <span className="text-[10px] font-bold text-white/40 uppercase tracking-tighter">Series</span>
            </div>
            <span className="text-base font-bold text-white leading-none">
              {setId ? '—' : (stats?.seriesCollected || 0)}
            </span>
          </div>

          <div className="flex flex-col min-w-[60px]">
            <div className="flex items-center gap-1.5 mb-0.5">
              <LayoutGrid size={12} className="text-white/40" />
              <span className="text-[10px] font-bold text-white/40 uppercase tracking-tighter">Sets</span>
            </div>
            <span className="text-base font-bold text-white leading-none">
              {setId ? '—' : (stats?.setsCollected || 0)}
            </span>
          </div>

          <div className="flex flex-col min-w-[80px] bg-green-500/5 border border-green-500/20 rounded-lg p-2 px-3">
            <div className="flex items-center gap-1.5 mb-0.5">
              <CheckCircle2 size={12} className="text-green-500" />
              <span className="text-[10px] font-bold text-green-500 uppercase tracking-tighter">Complete</span>
            </div>
            <span className="text-base font-bold text-white leading-none">
              {clampedCompletion.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}